import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { trackFeatureUsage } from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Gift,
  Copy,
  Check,
  Users,
  Mail,
  Send,
  Trophy,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const ReferralPage = () => {
  const [referralData, setReferralData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [sending, setSending] = useState(false);

  const loadReferralData = useCallback(async () => {
    try {
      const [codeRes, referralsRes] = await Promise.all([
        axios.get(`${API}/api/referrals/code`),
        axios.get(`${API}/api/referrals`)
      ]);
      setReferralData(codeRes.data);
      setReferrals(referralsRes.data);
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferralData();
    trackFeatureUsage('referral', 'view').catch(() => {});
  }, [loadReferralData]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralData.referral_code);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/api/referrals/invite`, {
        referred_email: inviteForm.email,
        referred_name: inviteForm.name
      });
      toast.success('Invitation sent!');
      setInviteForm({ email: '', name: '' });
      loadReferralData();
      trackFeatureUsage('referral', 'send_invite').catch(() => {});
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-500/10';
      case 'pending': return 'text-yellow-500 bg-yellow-500/10';
      case 'expired': return 'text-zinc-500 bg-zinc-500/10';
      default: return 'text-zinc-500 bg-zinc-500/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'pending': return Clock;
      case 'expired': return XCircle;
      default: return Clock;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="referral-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Earn Rewards</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
            REFERRAL PROGRAM
          </h1>
          <p className="text-zinc-500 mt-2 font-body">Refer other gyms and earn free months</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-ba-surface border border-ba-border p-6 rounded-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Total Referrals</span>
              <Users className="w-5 h-5 text-red-500" />
            </div>
            <p className="font-heading text-4xl text-white">{referralData?.referral_count || 0}</p>
            <p className="text-zinc-500 text-sm mt-1">successful referrals</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-ba-surface border border-ba-border p-6 rounded-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Months Earned</span>
              <Trophy className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="font-heading text-4xl text-yellow-500">{referralData?.months_earned || 0}</p>
            <p className="text-zinc-500 text-sm mt-1">free months earned</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-ba-surface border border-ba-border p-6 rounded-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Until Next Reward</span>
              <Gift className="w-5 h-5 text-green-500" />
            </div>
            <p className="font-heading text-4xl text-white">{referralData?.referrals_until_next_reward || 3}</p>
            <p className="text-zinc-500 text-sm mt-1">more referrals needed</p>
          </motion.div>
        </div>

        {/* Referral Code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-red-600/20 to-red-900/20 border border-red-600/30 p-6 rounded-sm"
        >
          <h2 className="font-heading text-xl text-white mb-4">Your Referral Code</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 bg-ba-black border border-ba-border rounded-sm p-4 flex items-center justify-between">
              <span className="font-heading text-2xl sm:text-3xl text-white tracking-wider">
                {referralData?.referral_code || 'Loading...'}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCopyCode}
                className="text-zinc-400 hover:text-white"
              >
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>
            <Button
              onClick={handleCopyCode}
              className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Code
            </Button>
          </div>
          <p className="text-zinc-400 text-sm mt-4">
            Share this code with other gym owners. When they sign up and subscribe, you both benefit!
          </p>
        </motion.div>

        {/* Send Invite */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-ba-surface border border-ba-border p-6 rounded-sm"
        >
          <h2 className="font-heading text-xl text-white mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-red-500" />
            Send an Invitation
          </h2>
          <form onSubmit={handleSendInvite} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Gym Owner Name</Label>
                <Input
                  placeholder="John Smith"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-ba-black border-ba-border"
                  data-testid="referral-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Email Address</Label>
                <Input
                  type="email"
                  placeholder="john@theirgym.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-ba-black border-ba-border"
                  data-testid="referral-email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
              data-testid="send-invite-btn"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </form>
        </motion.div>

        {/* Referral History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-ba-surface border border-ba-border rounded-sm"
        >
          <div className="p-6 border-b border-ba-border">
            <h2 className="font-heading text-xl text-white">Referral History</h2>
          </div>
          
          {referrals.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">No referrals yet. Start sharing your code!</p>
            </div>
          ) : (
            <div className="divide-y divide-ba-border">
              {referrals.map((referral) => {
                const StatusIcon = getStatusIcon(referral.status);
                return (
                  <div key={referral.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-zinc-500" />
                      </div>
                      <div>
                        <p className="text-white font-body">{referral.referred_name}</p>
                        <p className="text-zinc-500 text-sm">{referral.referred_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-500 text-sm hidden sm:block">
                        {new Date(referral.created_at).toLocaleDateString('en-US')}
                      </span>
                      <span className={cn(
                        "px-3 py-1 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1",
                        getStatusColor(referral.status)
                      )}>
                        <StatusIcon className="w-3 h-3" />
                        {referral.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-ba-surface border border-ba-border p-6 rounded-sm"
        >
          <h2 className="font-heading text-xl text-white mb-6">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="font-heading text-2xl text-red-500">1</span>
              </div>
              <h3 className="font-heading text-white mb-2">Share Your Code</h3>
              <p className="text-zinc-500 text-sm">Send your referral code to other MMA gym owners</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="font-heading text-2xl text-red-500">2</span>
              </div>
              <h3 className="font-heading text-white mb-2">They Sign Up</h3>
              <p className="text-zinc-500 text-sm">They use your code when registering for Black Arrow</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="font-heading text-2xl text-red-500">3</span>
              </div>
              <h3 className="font-heading text-white mb-2">Earn Rewards</h3>
              <p className="text-zinc-500 text-sm">Get 1 free month for every 3 successful referrals</p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default ReferralPage;
